import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { FileAction } from '@/shared/types';
import { DropdownMenu, MenuItem } from '@/shared/uikit/DropdownMenu';
import { Icon } from '@/shared/uikit/Icon';
import { numbers } from '@/shared/utils/numbers';

import { FileItemInfo } from '../types';

import { Status } from './Status';

export type FileItemType = 'photoItem' | 'fileItem';

type FileItemProps = {
  fileInfo: FileItemInfo;
  onRemove: () => void;
  onChangeComment: () => void;
  itemType?: FileItemType;
  allowedActions?: FileAction[];
};

export const FileItem: FC<FileItemProps> = ({
  fileInfo,
  onRemove,
  onChangeComment,
  allowedActions,
  itemType,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'uploadFiles' });
  const file = fileInfo.file;

  const hasError = !fileInfo.isValidType || !fileInfo.isValidSize || fileInfo.isUploadError;
  const errorMessage = useMemo(() => {
    if (!fileInfo.isValidType) return t('errors.fileType');
    if (!fileInfo.isValidSize) return t('errors.fileSize');
    if (fileInfo.isUploadError) return t('errors.uploadError');
    return null;
  }, [fileInfo]);

  const actions: Array<MenuItem & { key: FileAction }> = [
    {
      key: 'comment',
      label: 'Add comment',
      icon: 'chat-text',
      onClick: onChangeComment,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: 'trash',
      color: 'red',
      onClick: onRemove,
    },
  ];

  const filteredActions = allowedActions
    ? actions.filter((action) => allowedActions.includes(action.key))
    : actions;

  const fileItemInfo =
    itemType === 'photoItem' ? (
      <span style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <span className="truncate" style={{ fontWeight: 700, maxWidth: 'calc(100% - 70px)' }}>
          {file.name}
        </span>
        <span>{numbers.formatMemoryFromBytes(file.size)}</span>
      </span>
    ) : (
      <>
        <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <span className="truncate" style={{ fontWeight: 700, maxWidth: 'calc(100% - 70px)' }}>
            {file.name}
          </span>
          &nbsp;•&nbsp;
          <span>{numbers.formatMemoryFromBytes(file.size)}</span>
        </span>
        <span style={{ fontSize: 12 }}>
          {!hasError ? (
            <span className="lineclamp2" style={{ overflow: 'hidden' }}>
              {fileInfo.uploading ? t('uploading') : fileInfo.comment || '-'}
            </span>
          ) : (
            <span className="errorMessage">{errorMessage}</span>
          )}
        </span>
      </>
    );

  return (
    <Container className={cn({ error: hasError })}>
      <Status fileInfo={fileInfo} />
      <Info>{fileItemInfo}</Info>
      <Actions>
        {hasError || fileInfo.uploading ? (
          <MenuButton onClick={onRemove}>
            <Icon name="x-circle" size="s20" />
          </MenuButton>
        ) : (
          <DropdownMenu
            items={filteredActions}
            // overlayStyle={{ width: 144, paddingInline: 16 }}
          >
            <MenuButton>
              <Icon name="dots-three-vertical" size="s20" />
            </MenuButton>
          </DropdownMenu>
        )}
      </Actions>
    </Container>
  );
};

const Container = styled.div`
  min-height: 64px;
  padding: 14px 8px;
  border-bottom: 1px solid ${COLORS.systemDark100};

  display: grid;
  grid-template-columns: 28px 1fr 36px;
  gap: 8px;
  align-items: flex-start;

  & .errorMessage {
    color: ${COLORS.systemFlame200};
  }
`;

const Info = styled.div`
  overflow: hidden;
  display: grid;
  gap: 4px;
`;

const Actions = styled.div``;

const MenuButton = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.systemLight200};
  cursor: pointer;
`;
